/**
 * DialogScene — Full dialog scene with branching conversation tree.
 *
 * PixiJS canvas: minimal (dark overlay on current scene background).
 * HTML/CSS DOM: dialog box, speaker, portrait, text with typewriter effect,
 * option buttons with focus navigation, check results, reward popups.
 *
 * @module DialogScene
 */

import { Container, Graphics } from 'pixi.js';
import debugLog from '../debug/DebugLog.js';
import sceneManager from '../core/SceneManager.js';
import eventBus from '../core/EventBus.js';
import focusManager from '../input/FocusManager.js';
import dialogSystem from '../systems/DialogSystem.js';

/** Typewriter speed: milliseconds per character */
const TYPEWRITER_SPEED = 30;

/** How long to show check result popup (ms) */
const CHECK_POPUP_DURATION = 2000;

/** How long to show reward popup (ms) */
const REWARD_POPUP_DURATION = 2000;

export default class DialogScene {
  constructor() {
    this.container = new Container();

    // PixiJS overlay
    this._overlay = null;

    // Viewport
    this._width = 800;
    this._height = 600;

    // DOM elements
    this._dialogOverlay = null;
    this._dialogContainer = null;
    this._titleBarEl = null;
    this._boxEl = null;
    this._portraitEl = null;
    this._speakerEl = null;
    this._textEl = null;
    this._skipHintEl = null;
    this._optionsEl = null;
    this._endMessageEl = null;

    // Scene data
    this._returnScene = 'WorldTravel';
    this._returnData = {};
    this._dialogId = null;

    // Typewriter state
    this._typewriterText = '';
    this._typewriterIndex = 0;
    this._typewriterTimer = null;
    this._typewriterComplete = false;

    // Popup timers
    this._checkPopupTimer = null;
    this._rewardPopupTimer = null;

    // Event handler references
    this._eventHandlers = {};

    // Flags for context tracking
    this._contextFlags = {};
  }

  async init() {
    debugLog.info('DialogScene: init');
  }

  /**
   * Enter the dialog scene.
   * @param {object} data - { dialogId, party, character, flags, returnScene, returnData }
   */
  async enter(data) {
    debugLog.event('DialogScene: enter');

    // Get viewport
    const canvas = document.getElementById('game-canvas');
    if (canvas) {
      this._width = canvas.clientWidth || 800;
      this._height = canvas.clientHeight || 600;
    }

    // Store return info
    this._returnScene = data?.returnScene || 'WorldTravel';
    this._returnData = data?.returnData || {};
    this._dialogId = data?.dialogId || null;

    // Set up context flags (mutable object so dialog system can modify them)
    this._contextFlags = data?.flags ? { ...data.flags } : {};

    // Build PixiJS overlay (dark tint)
    this._buildOverlay();

    // Build DOM
    this._buildDOM();

    // Subscribe to events
    this._subscribeEvents();

    // Start dialog
    if (this._dialogId) {
      const context = {
        party: data?.party || null,
        character: data?.character || null,
        flags: this._contextFlags,
      };

      const started = dialogSystem.startDialog(this._dialogId, context);
      if (!started) {
        debugLog.error(`DialogScene: Failed to start dialog "${this._dialogId}"`);
        this._returnToPrevious();
        return;
      }

      this._showCurrentNode();
    }

    // Expose for testing
    if (window.__debug) {
      window.__debug.dialogSystem = dialogSystem;
      window.__debug.dialogScene = this;
    }
  }

  exit() {
    debugLog.event('DialogScene: exit');

    this._unsubscribeEvents();

    // Clear typewriter
    this._stopTypewriter();

    // Clear popup timers
    if (this._checkPopupTimer) {
      clearTimeout(this._checkPopupTimer);
      this._checkPopupTimer = null;
    }
    if (this._rewardPopupTimer) {
      clearTimeout(this._rewardPopupTimer);
      this._rewardPopupTimer = null;
    }

    // Remove DOM
    if (this._dialogOverlay && this._dialogOverlay.parentNode) {
      this._dialogOverlay.parentNode.removeChild(this._dialogOverlay);
    }
    this._dialogOverlay = null;

    if (this._dialogContainer && this._dialogContainer.parentNode) {
      this._dialogContainer.parentNode.removeChild(this._dialogContainer);
    }
    this._dialogContainer = null;

    // Remove any lingering popups
    const uiRoot = document.getElementById('ui-root');
    if (uiRoot) {
      uiRoot.querySelectorAll('.dialog-check-result, .dialog-reward-popup').forEach(el => el.remove());
    }

    focusManager.clear();
  }

  update(dt) {
    // Nothing to update per frame for dialog
  }

  resize(w, h) {
    this._width = w;
    this._height = h;
    if (this._overlay) {
      this._overlay.clear();
      this._overlay.rect(0, 0, w, h);
      this._overlay.fill({ color: 0x000000, alpha: 0.5 });
    }
  }

  // ── PixiJS Overlay ─────────────────────────────────

  _buildOverlay() {
    this._overlay = new Graphics();
    this._overlay.rect(0, 0, this._width, this._height);
    this._overlay.fill({ color: 0x000000, alpha: 0.5 });
    this.container.addChild(this._overlay);
  }

  // ── DOM Construction ───────────────────────────────

  _buildDOM() {
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    // Dark overlay (click to skip typewriter)
    this._dialogOverlay = document.createElement('div');
    this._dialogOverlay.className = 'dialog-overlay';
    this._dialogOverlay.addEventListener('click', () => this._onOverlayClick());
    uiRoot.appendChild(this._dialogOverlay);

    // Main container
    this._dialogContainer = document.createElement('div');
    this._dialogContainer.className = 'dialog-container';
    this._dialogContainer.id = 'dialog-container';
    this._dialogContainer.setAttribute('data-scene', 'Dialog');

    // Title bar
    this._titleBarEl = document.createElement('div');
    this._titleBarEl.className = 'dialog-title-bar';
    this._dialogContainer.appendChild(this._titleBarEl);

    // Dialog box (portrait + text)
    this._boxEl = document.createElement('div');
    this._boxEl.className = 'dialog-box';

    // Portrait
    this._portraitEl = document.createElement('div');
    this._portraitEl.className = 'dialog-portrait';
    this._portraitEl.innerHTML = '<span class="dialog-portrait-placeholder">?</span>';
    this._boxEl.appendChild(this._portraitEl);

    // Text area
    const textArea = document.createElement('div');
    textArea.className = 'dialog-text-area';

    this._speakerEl = document.createElement('div');
    this._speakerEl.className = 'dialog-speaker';
    textArea.appendChild(this._speakerEl);

    this._textEl = document.createElement('div');
    this._textEl.className = 'dialog-text';
    textArea.appendChild(this._textEl);

    this._skipHintEl = document.createElement('div');
    this._skipHintEl.className = 'dialog-skip-hint';
    this._skipHintEl.textContent = 'Click or press Enter to continue';
    this._skipHintEl.style.display = 'none';
    textArea.appendChild(this._skipHintEl);

    this._boxEl.appendChild(textArea);
    this._dialogContainer.appendChild(this._boxEl);

    // Options area
    this._optionsEl = document.createElement('div');
    this._optionsEl.className = 'dialog-options';
    this._optionsEl.id = 'dialog-options';
    this._optionsEl.style.display = 'none';
    this._dialogContainer.appendChild(this._optionsEl);

    // End message
    this._endMessageEl = document.createElement('div');
    this._endMessageEl.className = 'dialog-end-message';
    this._endMessageEl.style.display = 'none';
    this._dialogContainer.appendChild(this._endMessageEl);

    uiRoot.appendChild(this._dialogContainer);
  }

  // ── Display Logic ──────────────────────────────────

  _showCurrentNode() {
    const node = dialogSystem.getCurrentNode();
    const dialog = dialogSystem.getCurrentDialog();
    if (!node || !dialog) return;

    // Update title
    this._titleBarEl.textContent = dialog.title || '';

    // Update portrait
    if (dialog.portrait) {
      this._portraitEl.innerHTML = `<img src="${dialog.portrait}" alt="portrait">`;
    } else if (node.speaker) {
      this._portraitEl.innerHTML = `<span class="dialog-portrait-placeholder">${node.speaker.charAt(0)}</span>`;
    } else {
      this._portraitEl.innerHTML = '<span class="dialog-portrait-placeholder">...</span>';
    }

    // Update speaker
    this._speakerEl.textContent = node.speaker || '';
    this._speakerEl.style.display = node.speaker ? 'block' : 'none';

    // Hide options while typing
    this._optionsEl.style.display = 'none';
    this._endMessageEl.style.display = 'none';

    // Start typewriter for text
    this._startTypewriter(node.text || '');
  }

  _showOptions() {
    const options = dialogSystem.getAvailableOptions();
    const isEnd = dialogSystem.isComplete();

    if (isEnd) {
      // Show end message with "continue" option
      this._optionsEl.style.display = 'none';
      this._endMessageEl.style.display = 'block';
      this._endMessageEl.textContent = 'Press Enter or click to continue...';
      this._endMessageEl.setAttribute('data-focusable', 'true');
      this._endMessageEl.onclick = () => this._returnToPrevious();

      // Set up focus
      focusManager.clear();
      focusManager.pushGroup('dialog-end', this._dialogContainer);
      return;
    }

    // Build option buttons
    this._optionsEl.innerHTML = '';
    this._optionsEl.style.display = 'flex';

    let visibleIndex = 0;
    for (const opt of options) {
      // Skip hidden options that haven't been revealed
      if (opt.hidden && !opt.revealed) continue;

      const btn = document.createElement('button');
      btn.className = 'dialog-option';
      btn.setAttribute('data-focusable', 'true');
      btn.setAttribute('data-option-index', String(opt.index));

      // Build option text
      let displayText = '';

      // Option number
      visibleIndex++;
      const numSpan = document.createElement('span');
      numSpan.className = 'dialog-option-number';
      numSpan.textContent = String(visibleIndex);
      btn.appendChild(numSpan);

      // Main text (with check coloring)
      const textSpan = document.createElement('span');
      if (opt.check) {
        // Color the check prefix
        const checkPrefix = this._extractCheckPrefix(opt.text);
        if (checkPrefix) {
          const prefixSpan = document.createElement('span');
          prefixSpan.className = opt.meetsCheck ? 'check-passable' : 'check-difficult';
          prefixSpan.textContent = checkPrefix;
          textSpan.appendChild(prefixSpan);
          textSpan.appendChild(document.createTextNode(opt.text.slice(checkPrefix.length)));
        } else {
          textSpan.textContent = opt.text;
        }
      } else {
        textSpan.textContent = opt.text;
      }
      btn.appendChild(textSpan);

      // Mark unavailable options
      if (!opt.available) {
        btn.classList.add('option-unavailable');
        if (opt.requirementText) {
          const reqSpan = document.createElement('span');
          reqSpan.className = 'dialog-option-requirement';
          reqSpan.textContent = `(${opt.requirementText})`;
          btn.appendChild(reqSpan);
        }
      }

      // Mark hidden-revealed options
      if (opt.hidden && opt.revealed) {
        btn.classList.add('option-hidden-revealed');
      }

      // Click handler
      if (opt.available) {
        btn.addEventListener('click', () => this._onOptionSelected(opt.index));
      }

      this._optionsEl.appendChild(btn);
    }

    // Set up focus manager for options
    focusManager.clear();
    focusManager.pushGroup('dialog-options', this._optionsEl);
  }

  /**
   * Extract the check prefix like "[CHA 14]" from option text.
   * @param {string} text
   * @returns {string|null}
   */
  _extractCheckPrefix(text) {
    const match = text.match(/^\[.+?\]\s*/);
    return match ? match[0] : null;
  }

  // ── Typewriter ─────────────────────────────────────

  _startTypewriter(text) {
    this._stopTypewriter();
    this._typewriterText = text;
    this._typewriterIndex = 0;
    this._typewriterComplete = false;
    this._textEl.textContent = '';
    this._skipHintEl.style.display = 'block';

    this._typewriterTimer = setInterval(() => {
      if (this._typewriterIndex < this._typewriterText.length) {
        this._typewriterIndex++;
        this._textEl.innerHTML =
          this._typewriterText.slice(0, this._typewriterIndex) +
          '<span class="typewriter-cursor"></span>';
      } else {
        this._completeTypewriter();
      }
    }, TYPEWRITER_SPEED);
  }

  _completeTypewriter() {
    this._stopTypewriter();
    this._typewriterComplete = true;
    this._textEl.textContent = this._typewriterText;
    this._skipHintEl.style.display = 'none';

    // Show options after typewriter completes
    this._showOptions();
  }

  _stopTypewriter() {
    if (this._typewriterTimer) {
      clearInterval(this._typewriterTimer);
      this._typewriterTimer = null;
    }
  }

  _skipTypewriter() {
    if (!this._typewriterComplete && this._typewriterText) {
      this._completeTypewriter();
    }
  }

  // ── Event Handlers ─────────────────────────────────

  _subscribeEvents() {
    this._eventHandlers.onNodeChanged = (data) => this._onNodeChanged(data);
    this._eventHandlers.onCheckPass = (data) => this._showCheckResult(data, true);
    this._eventHandlers.onCheckFail = (data) => this._showCheckResult(data, false);
    this._eventHandlers.onReward = (data) => this._showReward(data);
    this._eventHandlers.onDialogEnd = () => {}; // Handled by node display

    eventBus.on('dialog:node-changed', this._eventHandlers.onNodeChanged);
    eventBus.on('dialog:check-pass', this._eventHandlers.onCheckPass);
    eventBus.on('dialog:check-fail', this._eventHandlers.onCheckFail);
    eventBus.on('dialog:reward', this._eventHandlers.onReward);

    // Keyboard/action handlers
    this._eventHandlers.onConfirm = () => this._onConfirmAction();
    this._eventHandlers.onCancel = () => this._onCancelAction();

    eventBus.on('action:confirm', this._eventHandlers.onConfirm);
    eventBus.on('action:cancel', this._eventHandlers.onCancel);
  }

  _unsubscribeEvents() {
    if (this._eventHandlers.onNodeChanged) {
      eventBus.off('dialog:node-changed', this._eventHandlers.onNodeChanged);
    }
    if (this._eventHandlers.onCheckPass) {
      eventBus.off('dialog:check-pass', this._eventHandlers.onCheckPass);
    }
    if (this._eventHandlers.onCheckFail) {
      eventBus.off('dialog:check-fail', this._eventHandlers.onCheckFail);
    }
    if (this._eventHandlers.onReward) {
      eventBus.off('dialog:reward', this._eventHandlers.onReward);
    }
    if (this._eventHandlers.onConfirm) {
      eventBus.off('action:confirm', this._eventHandlers.onConfirm);
    }
    if (this._eventHandlers.onCancel) {
      eventBus.off('action:cancel', this._eventHandlers.onCancel);
    }
    this._eventHandlers = {};
  }

  _onNodeChanged(data) {
    // Small delay before showing node to let check result display
    const delay = dialogSystem.getLastCheckResult() ? 800 : 0;
    setTimeout(() => {
      this._showCurrentNode();
    }, delay);
  }

  _onOptionSelected(index) {
    dialogSystem.selectOption(index);
  }

  _onConfirmAction() {
    if (!this._typewriterComplete) {
      this._skipTypewriter();
    } else if (dialogSystem.isComplete()) {
      this._returnToPrevious();
    }
    // If options are showing, FocusManager handles confirm via click
  }

  _onCancelAction() {
    if (!this._typewriterComplete) {
      this._skipTypewriter();
    }
    // B/Escape skips typewriter but doesn't close dialog mid-conversation
  }

  _onOverlayClick() {
    if (!this._typewriterComplete) {
      this._skipTypewriter();
    }
  }

  // ── Check Result Display ───────────────────────────

  _showCheckResult(data, success) {
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    // Remove existing popup
    uiRoot.querySelectorAll('.dialog-check-result').forEach(el => el.remove());

    const popup = document.createElement('div');
    popup.className = `dialog-check-result ${success ? 'check-success' : 'check-fail'}`;

    const attrName = (data.attribute || '???').toUpperCase();
    const resultText = success ? 'Success!' : 'Failed!';

    popup.innerHTML = `
      <div>${resultText}</div>
      <div class="check-roll-detail">
        Roll: ${data.roll} + ${data.modifier} = ${data.total} vs DC ${data.dc}
      </div>
    `;

    uiRoot.appendChild(popup);

    // Auto-remove after animation
    if (this._checkPopupTimer) clearTimeout(this._checkPopupTimer);
    this._checkPopupTimer = setTimeout(() => {
      if (popup.parentNode) popup.remove();
    }, CHECK_POPUP_DURATION);
  }

  // ── Reward Display ─────────────────────────────────

  _showReward(data) {
    if (!data) return;

    let rewardText = '';
    if (data.type === 'gold' && data.amount) {
      if (data.amount > 0) {
        rewardText = `+${data.amount} Gold`;
      } else {
        rewardText = `${data.amount} Gold`;
      }
    } else if (data.type === 'items' && data.items) {
      rewardText = `Received: ${data.items.join(', ')}`;
    } else if (data.type === 'quest') {
      rewardText = `New Quest: ${data.questId}`;
    } else if (data.type === 'flags') {
      // Don't show popup for flags
      return;
    }

    if (!rewardText) return;

    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    const popup = document.createElement('div');
    popup.className = 'dialog-reward-popup';
    popup.textContent = rewardText;
    uiRoot.appendChild(popup);

    if (this._rewardPopupTimer) clearTimeout(this._rewardPopupTimer);
    this._rewardPopupTimer = setTimeout(() => {
      if (popup.parentNode) popup.remove();
    }, REWARD_POPUP_DURATION);
  }

  // ── Navigation ─────────────────────────────────────

  _returnToPrevious() {
    // End dialog if still active
    if (!dialogSystem.isComplete()) {
      dialogSystem.endDialog();
    }

    debugLog.event(`DialogScene: Returning to ${this._returnScene}`);
    sceneManager.transition(this._returnScene, this._returnData);
  }
}
